import { css } from '@emotion/core'
import React from 'react'

import { HEADER_HEIGHT } from '../constants/header'
import Chevron from '../icons/abstract/chevron.svg'
import { AnchorComponentProps, AnchorProps, subAnchorLinkStyle } from './Anchors'

// export for ScrollToTop
export const anchorsMobileNavigationHeight = 50 // px

const AnchorsMobileNavigation: React.FunctionComponent<
  AnchorComponentProps & {
    currentAnchor: string
    currentSubAnchor?: string
    setCurrentSubAnchor?: React.Dispatch<React.SetStateAction<string>>
    onClickHandlerAnchors: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, anchor: AnchorProps) => void
    onClickHandlerSubAnchors: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, subAnchorId: string) => void
  }
> = ({
  anchors,
  title,
  currentAnchor,
  currentSubAnchor,
  setCurrentSubAnchor,
  onClickHandlerAnchors,
  onClickHandlerSubAnchors,
  blackBG = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (e.target instanceof Node) {
        if (ref.current && !ref.current.contains(e.target)) {
          setIsOpen(false)
        }
      }
    }

    document.addEventListener('click', checkIfClickedOutside)

    return () => {
      document.removeEventListener('click', checkIfClickedOutside)
    }
  }, [])

  // Anchors should only be visible when RichText is visible
  // and until footer is visible
  const [isVisible, setIsVisible] = React.useState(false)
  React.useEffect(() => {
    const handleScroll = () => {
      const richText = document.getElementsByClassName('rich-text')[0]
      const footer = document.getElementById('footer')
      // show Anchors when RichText is scrollend into view
      if (richText && richText.getBoundingClientRect().top < 0.8 * window.innerHeight) {
        setIsVisible(true)
      } else {
        setIsVisible(false)
        setIsOpen(false)
      }

      // hide Anchors when footer is scrolled into view
      if (footer && footer.getBoundingClientRect().top < window.innerHeight) {
        setIsVisible(false)
        setIsOpen(false)
      }
    }

    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  })

  return (
    <>
      <div css={overlayContainerStyle(isOpen)} />
      <div
        ref={ref}
        className={'full-screen-fixed with-backdrop-filter'}
        onClick={() => setIsOpen(!isOpen)}
        css={anchorsMobileContainerStyle(blackBG, isVisible, isOpen)}
      >
        <div className={'heading4'} css={titleStyle(blackBG, isOpen)}>
          {title} <Chevron css={chevronStyle(blackBG, isOpen)} />
        </div>
        <ul id={'anchors-mobile-container'} css={anchorListStyle(isOpen)}>
          {anchors.map((anchor, index) => {
            const isCurrent = currentAnchor === anchor.id
            return (
              <li
                key={`anchor-item-${index}`}
                id={`anchor-item-${anchor.id}`}
                css={anchorListItemStyle(index === 0, index === anchors.length - 1)}
              >
                <div key={anchor.id}>
                  <a
                    className={`body-2 ${isCurrent ? 'blue-text bold' : blackBG ? 'white-text' : 'black-text'}`}
                    href={`#${anchor.id}/`}
                    onClick={(e) => onClickHandlerAnchors(e, anchor)}
                  >
                    {anchor.label}
                  </a>
                </div>
                {anchor.id === currentAnchor && setCurrentSubAnchor && anchor.subAnchors && (
                  <ul css={subAnchorListStyle}>
                    {anchor.subAnchors.map((subAnchor) => (
                      <li key={subAnchor.id} css={subAnchorListItemStyle}>
                        <a
                          className={'body-2 black-text opacity-on-hover'}
                          css={subAnchorLinkStyle(currentSubAnchor === subAnchor.id)}
                          onClick={(e) => onClickHandlerSubAnchors(e, subAnchor.id)}
                        >
                          {subAnchor.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </>
  )
}

export default AnchorsMobileNavigation

const overlayContainerStyle = (isOpen: boolean) => css`
  pointer-events: none;
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: #00000080;
  opacity: ${isOpen ? 1 : 0};
  transition: all 0.3s ease-in-out;
`

const anchorsMobileContainerStyle = (blackBG: boolean, isVisible: boolean, isOpen: boolean) => css`
  position: fixed;
  bottom: 0;
  width: 100vw;
  box-sizing: border-box;
  padding: 1rem 0 0;
  // matches rgba(255, 255, 255, 0.1) without opacity
  ${blackBG && 'background: #39414E99'};
  ${!blackBG && isOpen && 'background: #fff !important'};
  border-top: 1px solid ${blackBG ? '#fff' : '#318eff'};
  z-index: 3;
  box-shadow: rgb(0 0 0 / 10%) 0 -0.5rem 1rem;
  transition: all 0.3s ease-in-out;

  ${isVisible
    ? `
      opacity: 1;
      transform: translateY(0);
    `
    : `
      opacity: 0;
      transform: translateY(100%);
  `}
`

const titleStyle = (blackBG: boolean, isOpen: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 -1.5rem;
  padding: 0 1.5rem 1rem;
  border-bottom: 1px solid ${isOpen ? (blackBG ? 'rgba(255, 255, 255, 0.1)' : '#efeff0') : 'transparent'};
`

const chevronStyle = (blackBG: boolean, isDropDownOpen: boolean) => css`
  transform: rotate(${isDropDownOpen ? '0' : '180deg'});
  transition: transform 0.3s ease;

  ${blackBG && ' path { stroke: #fff; }'};
`

const anchorListStyle = (isOpen: boolean) => css`
  max-height: ${isOpen ? `calc(50vh - ${HEADER_HEIGHT}px)` : '0'};
  overflow: ${isOpen ? 'scroll' : 'hidden'};
  transition: all 0.3s ease-in-out;
`

const anchorListItemStyle = (firstItem: boolean, lastItem: boolean) => css`
  margin: 0.75rem 0;
  ${firstItem && 'margin-top: 1em;'}
  ${lastItem && 'margin-bottom: 1.25rem;'}
`

const subAnchorListStyle = css`
  padding-left: 1.25rem;
  margin-bottom: 1rem;
`

const subAnchorListItemStyle = css`
  margin: 0.75rem 0;
`
